import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getInspections,
  getInspectionAnalytics,
  createInspection,
  deleteInspection,
  completeInspection,
  getInspectionAttendees,
  convertAttendeeToLead,
  checkInBuyerToSession,
  searchInspectionBuyers,
  getInspectionRequests,
  confirmInspectionRequest,
  cancelInspectionRequest
} from '../../../api/inspection';
import { getProperties, checkBackendConnection, NETWORK_ERROR_MESSAGE } from '../../../api';
import { getAgents } from '../../../api/agent';
import { apiCache, cacheKeys, cachedApiCall, invalidateCache } from '../../../lib/cache';
import { formatApiError } from '../../../helper/function';
import {
  Calendar,
  Clock,
  Users,
  MapPin,
  QrCode,
  Trash2,
  Eye,
  Plus,
  CheckCircle,
  TrendingUp,
  DollarSign,
  Building,
  UserPlus,
  X,
  RefreshCw,
  Home,
  User,
  Edit,
  Mail,
  Phone,
  Inbox,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import moment from 'moment';
import CreateOpenHomeModal from '../components/CreateOpenHomeModal';
import { schedulableProperties } from '../utils/schedulableProperties';
import { reportCalendarSync } from '../utils/calendarSync';
import { inspectionTerms } from '../utils/inspectionTerms';
import {
  MELBOURNE_TZ,
  melbourneWallTimeToUtcISOString,
  formatMelbourneDatePart,
  formatMelbourneTimePart,
  formatMelbourneClock,
  formatMelbourneDayLabel,
  formatMelbourneDateTime,
} from '../../../utils/melbourneTime';
import OpenHomeQrModal from '../components/OpenHomeQrModal';
import AgentPortalQrModal from '../components/AgentPortalQrModal';
import AgencyScopeSwitch, {
  useAgencyScope,
  useExtraAgents,
  IncludeAgentsPicker,
} from '../../../components/common/AgencyScopeSwitch';
import TimeInput from '../components/TimeInput';
import DateInput from '../../../components/common/DateInput';

// Skeleton Loader Components
const SkeletonCard = () => (
  <div className="bg-white rounded-lg shadow p-3 sm:p-4 md:p-5 border border-gray-200 animate-pulse">
    <div className="flex items-center justify-between mb-3">
      <div className="h-4 bg-gray-200 rounded w-24"></div>
      <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
    </div>
    <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
    <div className="h-3 bg-gray-200 rounded w-32"></div>
  </div>
);

const SkeletonInspection = () => (
  <div className="bg-[#F9FAFB] rounded-xl border border-[#E4E6E9] p-6 animate-pulse">
    <div className="flex justify-between items-center">
      <div className="flex-1">
        <div className="h-5 bg-gray-200 rounded w-48 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-32 mb-3"></div>
        <div className="h-4 bg-gray-200 rounded w-40"></div>
      </div>
      <div className="w-24 h-9 bg-gray-200 rounded-lg"></div>
    </div>
  </div>
);

const InspectionsPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [showAgentQrModal, setShowAgentQrModal] = useState(false);
  const [inspections, setInspections] = useState([]);
  const [properties, setProperties] = useState([]);
  const [agents, setAgents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loadingStates, setLoadingStates] = useState({
    initial: true,
    inspections: false,
    analytics: false,
    properties: false,
    attendees: false,
    submit: false
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showOpenHomeModal, setShowOpenHomeModal] = useState(false);
  const [qrModalInspection, setQrModalInspection] = useState(null);
  const [editingInspectionId, setEditingInspectionId] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [showQR, setShowQR] = useState(null);
  const [activeTab, setActiveTab] = useState('Today'); // 'Upcoming', 'Today', 'Previous', 'Requests'
  const [inspectionRequests, setInspectionRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestActionId, setRequestActionId] = useState(null);
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState(null);
  const [showAddBuyerModal, setShowAddBuyerModal] = useState(false);
  const [showViewBuyersModal, setShowViewBuyersModal] = useState(false);
  const [viewingBuyersFor, setViewingBuyersFor] = useState(null);
  const [addingBuyerTo, setAddingBuyerTo] = useState(null);
  const [buyerFormData, setBuyerFormData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
    contract_requested: false,
    has_property_to_sell: false,
    temperature: 'WARM' // Default
  });

  // Form state
  const [formData, setFormData] = useState({
    property: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 30,
    notes: '',
    create_calendar_event: true
  });

  // Whose open homes this page is showing. Agents and admins get their own
  // (CRM-2026-00077 — Paul Jimoulis, 28 Sep 2026: "other agents do not need to
  // see my inspection times"); only the agency principal gets the office, and
  // only an admin can fold a named colleague in beside their own.
  const {
    agencyWide,
    setAgencyWide,
    canSwitch: canSwitchScope,
    canIncludeAgents,
  } = useAgencyScope('openHomes');
  const { agentIds: extraAgentIds, toggleAgentId, setAgentIds } = useExtraAgents(
    'openHomes',
    canIncludeAgents && !agencyWide
  );

  // One string, not the array/booleans — this is an effect and cache-key
  // dependency, and an object here would refetch on every render.
  const scopeKey = useMemo(
    () => `${agencyWide ? 'all' : 'mine'}:${[...extraAgentIds].sort().join(',')}`,
    [agencyWide, extraAgentIds]
  );

  const scopeParams = useMemo(() => {
    const params = {};
    if (canSwitchScope) params.agency_wide = agencyWide ? 'true' : 'false';
    if (!agencyWide && extraAgentIds.length) params.agents = extraAgentIds.join(',');
    return params;
  }, [canSwitchScope, agencyWide, extraAgentIds]);

  // Brand colors memoized
  const brand = useMemo(() => ({
    teal: '#55C5D0',
    tealHover: '#3bb3be',
    tealLight: '#E0F7FA'
  }), []);

  // Cache TTL configurations
  const cacheTTL = useMemo(() => ({
    inspections: 3 * 60 * 1000, // 3 minutes
    analytics: 5 * 60 * 1000,   // 5 minutes
    properties: 10 * 60 * 1000, // 10 minutes
    attendees: 2 * 60 * 1000    // 2 minutes
  }), []);

  // Cache key generators
  const getCacheKey = useCallback((type, filter = '') => {
    switch (type) {
      // Namespaced by scope: flipping "All agents" / "My records" has to fetch,
      // not re-serve the other view from the browser cache.
      case 'inspections':
        return `inspections-${filter}-${scopeKey}`;
      case 'analytics':
        return `inspections-analytics-${scopeKey}`;
      case 'properties':
        // -v2: the dropdown's request gained active_listings_only on 28 Sep 2026
        // (CRM-2026-00079). apiCache holds this list for 10 minutes and survives
        // a reload, so without a new key a browser that fetched before the fix
        // would keep serving the withdrawn listings the fix removes.
        return 'inspections-properties-v2';
      case 'attendees':
        return `inspection-attendees-${filter}`;
      default:
        return type;
    }
  }, [scopeKey]);

  // Check backend connection on component mount
  useEffect(() => {
    checkBackendConnection().then(result => {
      if (!result.connected) {
        console.error('⚠️ Backend connection check failed:', result.message);
        console.error('Backend URL:', result.url);
        toast.error(result.message, { duration: 4000 });
      } else {
        console.log('✅ Backend connection OK:', result.url);
      }
    });
  }, []);

  // Load initial data. scopeKey (a string) is the dependency, not scopeParams —
  // the object is a fresh identity on every render and would refetch forever.
  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, scopeKey]);

  // Agents for the Open Home modal's Primary/Secondary agent pickers.
  useEffect(() => {
    getAgents({ page_size: 100 })
      .then((res) => setAgents(res?.data?.results || res?.data || []))
      .catch(() => setAgents([]));
  }, []);

  // Lazy load properties when modal is opened
  useEffect(() => {
    if (showCreateModal && !propertiesLoaded) {
      loadProperties();
    }
  }, [showCreateModal, propertiesLoaded]);

  const loadInitialData = useCallback(async (forceRefresh = false) => {
    const params = { ...scopeParams };
    const cacheKeyInspections = getCacheKey('inspections', 'all');
    const cacheKeyAnalytics = getCacheKey('analytics');

    // Check if we have ANY cached data
    const hasCachedInspections = apiCache.has(cacheKeyInspections);
    const hasCachedAnalytics = apiCache.has(cacheKeyAnalytics);

    // Only show skeleton loaders if there's NO cached data at all OR force refresh
    const shouldShowSkeleton = !hasCachedInspections || !hasCachedAnalytics || forceRefresh;

    if (shouldShowSkeleton) {
      setLoadingStates(prev => ({ ...prev, initial: true, inspections: true, analytics: true }));
    }

    try {
      // Load inspections and analytics with stale-while-revalidate
      const [inspectionsRes, analyticsRes] = await Promise.all([
        cachedApiCall(
          cacheKeyInspections,
          () => getInspections(params),
          10 * 60 * 1000, // 10 minute cache
          {
            forceRefresh,
            onStaleData: (staleData) => {
              // Immediately show stale data
              setInspections(staleData.data.results || staleData.data);
              setLoadingStates(prev => ({ ...prev, initial: false, inspections: false }));
            }
          }
        ),
        cachedApiCall(
          cacheKeyAnalytics,
          () => getInspectionAnalytics(scopeParams),
          10 * 60 * 1000, // 10 minute cache
          {
            forceRefresh,
            onStaleData: (staleData) => {
              // Immediately show stale analytics
              setAnalytics(staleData.data);
              setLoadingStates(prev => ({ ...prev, analytics: false }));
            }
          }
        )
      ]);

      // Update with fresh data
      const inspectionsData = inspectionsRes.data.results || inspectionsRes.data || [];
      console.log(`Loaded ${inspectionsData.length} inspections`);
      console.log('Inspections data:', inspectionsData);
      setInspections(inspectionsData);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error code:', error.code);
      
      // Check for network errors
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || error.code === 'ERR_CONNECTION_REFUSED') {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        console.error(`Backend server appears to be down. Expected URL: ${apiUrl}`);
        toast.error(NETWORK_ERROR_MESSAGE, { duration: 4000 });
      } else if (error.response?.status === 401) {
        toast.error('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        toast.error('Permission denied. Check authentication.');
      } else {
        toast.error('Failed to load inspections data');
      }
      
      // Set empty arrays on error to prevent UI issues
      setInspections([]);
      setAnalytics(null);
    } finally {
      setLoadingStates(prev => ({ ...prev, initial: false, inspections: false, analytics: false }));
    }
  }, [getCacheKey, cacheTTL, scopeParams]);

  const loadProperties = useCallback(async () => {
    if (propertiesLoaded) return;

    const cacheKey = getCacheKey('properties');
    const hasCached = apiCache.get(cacheKey);

    if (!hasCached) {
      setLoadingStates(prev => ({ ...prev, properties: true }));
    }

    try {
      const response = await cachedApiCall(
        cacheKey,
        // Agents may only create an open home for a listing they are assigned
        // to, so the booking dropdown is scoped to "My Listings" — properties
        // where the logged-in user is the primary, secondary or third agent
        // (the default getProperties() scope). The backend enforces the same
        // rule on create.
        //
        // active_listings_only narrows that to the listings an open time can
        // actually be put out against - live On Market / Off Market only, no
        // withdrawn, sold, leased or appraisal rows (CRM-2026-00079). Filtering
        // server-side rather than after the fetch is what keeps `count` honest
        // and stops the dropped rows from paginating real listings off page 1;
        // exclude_sold is kept alongside it so an older backend still drops the
        // sold comps. A large page_size then holds the whole list in one page.
        () => getProperties({ active_listings_only: true, exclude_sold: true, page_size: 500 }),
        cacheTTL.properties,
        {
          onStaleData: (staleData) => {
            setProperties(staleData.data.results || staleData.data);
            setPropertiesLoaded(true);
            setLoadingStates(prev => ({ ...prev, properties: false }));
          }
        }
      );

      setProperties(response.data.results || response.data);
      setPropertiesLoaded(true);
    } catch (error) {
      console.error('Error loading properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoadingStates(prev => ({ ...prev, properties: false }));
    }
  }, [propertiesLoaded, getCacheKey, cacheTTL]);

  const handleCreateInspection = useCallback(async (e) => {
    e.preventDefault();
    setLoadingStates(prev => ({ ...prev, submit: true }));

    try {
      // Validate form data
      if (!formData.property || !formData.scheduled_date || !formData.scheduled_time) {
        toast.error('Please fill in all required fields');
        setLoadingStates(prev => ({ ...prev, submit: false }));
        return;
      }

      // Interpret the typed date/time as Melbourne wall-clock time (the agency
      // timezone), not the editor's browser timezone. Building off the browser
      // stored a non-Melbourne agent's time shifted by their UTC offset, which
      // is what then published to REA and the buyer portal.
      const scheduledDateTime = melbourneWallTimeToUtcISOString(
        formData.scheduled_date,
        formData.scheduled_time,
      );

      const data = {
        property: parseInt(formData.property),
        scheduled_date: scheduledDateTime,
        duration_minutes: formData.duration_minutes,
        status: 'scheduled',
        notes: formData.notes || '',
        create_calendar_event: formData.create_calendar_event
      };

      console.log('Creating/updating inspection with data:', data);

      let response;
      const wasEditing = !!editingInspectionId;
      if (editingInspectionId) {
        // Update existing inspection
        console.log('Updating inspection:', editingInspectionId);
        const { updateInspection } = await import('../../../api/inspection');
        response = await updateInspection(editingInspectionId, data);
        console.log('Inspection updated:', response.data);
        toast.success('Inspection updated successfully!');
        setEditingInspectionId(null);
      } else {
        // Create new inspection
        console.log('Creating new inspection');
        response = await createInspection(data);
        console.log('Inspection created:', response.data);
        toast.success('Open For Inspection scheduled successfully!');
      }

      // Report what happened on the hosting agents' Google Calendars — in
      // particular "no calendar connected", which is otherwise a silent no-op.
      reportCalendarSync(response?.data);

      // Warn when the open time couldn't reach REA because the listing is live
      // on REA but its Visibility box is unticked — otherwise it fails silently
      // (CRM-2026-00022, 74 Lucknow). Backend sets rea_warning on the response.
      if (response?.data?.rea_warning) {
        toast(response.data.rea_warning, { icon: '⚠️', duration: 8000 });
      }

      // Invalidate caches immediately (frontend + any related)
      apiCache.invalidatePattern('inspections-');
      apiCache.invalidatePattern('inspection-attendees-');
      apiCache.invalidate('inspections-analytics');
      apiCache.invalidate('dashboard_inspections');

      // Optimistically update local state with the response so the UI
      // reflects the change immediately (avoids any stale-cache race)
      if (wasEditing && response?.data?.id) {
        setInspections(prev => prev.map(item =>
          item.id === response.data.id ? { ...item, ...response.data } : item
        ));
      }

      // Wait a moment for backend cache to clear
      await new Promise(resolve => setTimeout(resolve, 300));

      setShowCreateModal(false);
      resetForm();

      // Force reload data from backend to show the new/updated inspection
      await loadInitialData(true);

      console.log('Inspection data reloaded');
    } catch (error) {
      console.error('Error saving OFI:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // Check for network errors (backend not running)
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || error.code === 'ERR_CONNECTION_REFUSED') {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        toast.error(NETWORK_ERROR_MESSAGE, { duration: 4000 });
        console.error(`Backend server appears to be down. Expected URL: ${apiUrl}`);
        console.error('To fix this:');
        console.error('1. Check if Django backend is running');
        console.error('2. Verify the backend URL in .env file (REACT_APP_API_URL)');
        console.error('3. Check backend logs for errors');
      } else if (error.response?.status === 401) {
        toast.error('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        // The backend raises 403 when an agent tries to book an OFI on a
        // listing they aren't assigned to — surface that reason rather than a
        // generic CSRF message.
        const forbiddenMsg =
          error.response?.data?.detail ||
          error.response?.data?.error;
        toast.error(forbiddenMsg || 'Permission denied. Check CSRF token and cookies.');
        console.error('OFI create forbidden:', error.response?.data);
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.message || 
                        error.response?.data?.detail || 
                        JSON.stringify(error.response?.data);
        toast.error(`Validation error: ${errorMsg}`);
      } else if (error.response) {
        toast.error(error.response?.data?.message || 
                   error.response?.data?.detail || 
                   error.message || 
                   'Failed to save inspection');
      } else {
        // No response at all (e.g. a timeout - axios reports that as
        // ECONNABORTED, a code the check above doesn't cover) - same
        // "reach the server" fallback rather than surfacing a raw axios
        // message like "timeout of 30000ms exceeded" to the agent.
        toast.error(NETWORK_ERROR_MESSAGE);
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, submit: false }));
    }
  }, [formData, editingInspectionId, loadInitialData]);

  const loadInspectionRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const response = await getInspectionRequests();
      setInspectionRequests(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error loading inspection requests:', error);
      toast.error('Failed to load inspection requests');
      setInspectionRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  // Load requests when the Requests tab is active
  useEffect(() => {
    if (activeTab === 'Requests') {
      loadInspectionRequests();
    }
  }, [activeTab, loadInspectionRequests]);

  const handleConfirmRequest = useCallback(async (req) => {
    setRequestActionId(req.id);
    try {
      await confirmInspectionRequest(req.id);
      toast.success(req.is_proposed ? 'Request confirmed and OFI scheduled' : 'Request confirmed');
      apiCache.invalidatePattern('inspections-');
      apiCache.invalidate('inspections-analytics');
      await loadInspectionRequests();
    } catch (error) {
      console.error('Error confirming request:', error);
      toast.error(error.response?.data?.message || 'Failed to confirm request');
    } finally {
      setRequestActionId(null);
    }
  }, [loadInspectionRequests]);

  const handleCancelRequest = useCallback(async (req) => {
    if (!window.confirm(`Cancel inspection request from ${req.name}?`)) return;
    setRequestActionId(req.id);
    try {
      await cancelInspectionRequest(req.id);
      toast.success('Request cancelled');
      await loadInspectionRequests();
    } catch (error) {
      console.error('Error cancelling request:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel request');
    } finally {
      setRequestActionId(null);
    }
  }, [loadInspectionRequests]);

  const handleDeleteInspection = useCallback((inspection) => {
    setInspectionToDelete(inspection);
    setShowDeleteModal(true);
  }, []);

  const confirmDeleteInspection = useCallback(async () => {
    if (!inspectionToDelete || isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteInspection(inspectionToDelete.id);

      // Invalidate caches
      apiCache.invalidatePattern('inspections-');
      apiCache.invalidate('inspections-analytics');

      toast.success('Inspection deleted successfully');
      setShowDeleteModal(false);
      setInspectionToDelete(null);
      loadInitialData();
    } catch (error) {
      console.error('Error deleting inspection:', error);
      toast.error('Failed to delete inspection');
    } finally {
      setIsDeleting(false);
    }
  }, [inspectionToDelete, isDeleting, loadInitialData]);

  const handleCompleteInspection = useCallback(async (inspectionId) => {
    const feedbackSummary = window.prompt('Enter feedback summary (optional):');

    try {
      await completeInspection(inspectionId, { feedback_summary: feedbackSummary });

      // Invalidate caches
      apiCache.invalidatePattern('inspections-');
      apiCache.invalidate('inspections-analytics');

      toast.success('Inspection marked as completed');
      loadInitialData();
    } catch (error) {
      console.error('Error completing inspection:', error);
      toast.error('Failed to complete inspection');
    }
  }, [loadInitialData]);

  const loadAttendees = useCallback(async (inspectionId) => {
    const cacheKey = getCacheKey('attendees', inspectionId);
    const hasCached = apiCache.get(cacheKey);

    if (!hasCached) {
      setLoadingStates(prev => ({ ...prev, attendees: true }));
    }

    try {
      const response = await cachedApiCall(
        cacheKey,
        () => getInspectionAttendees(inspectionId),
        cacheTTL.attendees,
        {
          onStaleData: (staleData) => {
            setAttendees(staleData.data);
            setSelectedInspection(inspectionId);
            setLoadingStates(prev => ({ ...prev, attendees: false }));
          }
        }
      );

      setAttendees(response.data);
      setSelectedInspection(inspectionId);
    } catch (error) {
      console.error('Error loading attendees:', error);
      toast.error('Failed to load attendees');
    } finally {
      setLoadingStates(prev => ({ ...prev, attendees: false }));
    }
  }, [getCacheKey, cacheTTL]);

  const handleConvertToLead = useCallback(async (attendeeId, attendeeName) => {
    if (!window.confirm(`Convert ${attendeeName} to a lead?`)) return;

    try {
      await convertAttendeeToLead(attendeeId);

      // Invalidate attendees cache
      apiCache.invalidatePattern('inspection-attendees-');

      toast.success('Attendee converted to lead successfully');
      loadAttendees(selectedInspection);
    } catch (error) {
      console.error('Error converting to lead:', error);
      toast.error('Failed to convert to lead');
    }
  }, [selectedInspection, loadAttendees]);

  const resetForm = useCallback(() => {
    setFormData({
      property: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 30,
      notes: '',
      create_calendar_event: true
    });
    setEditingInspectionId(null);
  }, []);

  const getCheckInUrl = useCallback((inspectionId) => {
    return `${window.location.origin}/ofi-checkin/${inspectionId}`;
  }, []);

  const getStatusColor = useCallback((status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }, []);

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleString('en-AU', {
      timeZone: MELBOURNE_TZ,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Memoized filtered inspection list
  const inspectionsList = useMemo(() => {
    // Bucket by Melbourne calendar day, not the viewer's browser day, so an
    // overseas-based agent sees the same Today/Upcoming/Previous split as the
    // Melbourne office (comparing "YYYY-MM-DD" strings is a safe day compare).
    const todayMel = formatMelbourneDatePart(new Date());
    const filtered = inspections.filter(inspection => {
      if (!inspection.scheduled_date) return false;
      const inspectionDayMel = formatMelbourneDatePart(inspection.scheduled_date);

      if (activeTab === 'Today') {
        return inspectionDayMel === todayMel;
      } else if (activeTab === 'Upcoming') {
        return inspectionDayMel > todayMel;
      } else if (activeTab === 'Previous') {
        return inspectionDayMel < todayMel;
      }
      return true;
    });

    // Upcoming: show the soonest inspections first (earliest date at the top)
    if (activeTab === 'Upcoming') {
      return filtered.sort((a, b) =>
        moment(a.scheduled_date).valueOf() - moment(b.scheduled_date).valueOf()
      );
    }
    return filtered;
  }, [inspections, activeTab]);

  // Initial loading state with centered skeleton
  if (loadingStates.initial) {
    return (
      <div className="p-3 sm:p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="h-10 bg-gray-200 rounded w-64 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-40 animate-pulse"></div>
        </div>



        <div className="bg-white rounded-lg shadow p-4 border border-gray-200 mb-6">
          <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
        </div>

        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <SkeletonInspection key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 md:p-8 space-y-4 sm:space-y-6 bg-white min-h-screen font-sans overflow-x-hidden">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="w-full sm:w-auto text-center sm:text-left">
          <h1 className="text-2xl sm:text-[28px] font-bold text-[#353B48]">Open Homes</h1>
          <p className="text-[#6B7280] text-sm mt-1">Run check-ins, qualify buyers and send post-visit follow-ups</p>
        </div>
        <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => setShowAgentQrModal(true)}
            className="w-full sm:w-auto bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-5 py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 transition-all text-sm"
            title="Your personal QR — invite people to sign up to the portal"
          >
            <QrCode className="w-4 h-4" />
            QR Link to Portal
          </button>
          <button
            onClick={() => {
              if (!propertiesLoaded) loadProperties();
              setShowOpenHomeModal(true);
            }}
            className="w-full sm:w-auto bg-[#4EADB7] hover:bg-[#3f9ba4] text-white px-5 py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 transition-all shadow-md text-sm"
          >
            <Plus className="w-4 h-4" />
            Create Open Home
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-[#E6E7E9] p-3 sm:p-4 mb-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs sm:text-sm font-medium text-[#6B7280] whitespace-nowrap">Filter by Status:</span>
            <div className="flex items-center gap-2">
              {canSwitchScope && (
                <AgencyScopeSwitch
                  agencyWide={agencyWide}
                  onChange={setAgencyWide}
                  mineLabel="My open homes"
                  agencyLabel="All agents"
                />
              )}
              {canIncludeAgents && !agencyWide && (
                <IncludeAgentsPicker
                  agents={agents}
                  selectedIds={extraAgentIds}
                  onToggle={toggleAgentId}
                  onClear={() => setAgentIds([])}
                  currentUserId={user?.id}
                />
              )}
            <button
              onClick={async () => {
                if (activeTab === 'Requests') {
                  await loadInspectionRequests();
                  toast.success('Requests refreshed');
                  return;
                }
                // Clear all inspection caches
                apiCache.invalidatePattern('inspections-');
                apiCache.invalidate('inspections-analytics');
                apiCache.invalidate('dashboard_inspections');
                await loadInitialData();
                toast.success('Inspections refreshed');
              }}
              className="px-2.5 sm:px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 transition-all"
              title="Refresh inspections"
            >
              <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {['Upcoming', 'Today', 'Previous', 'Requests'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex-shrink-0 ${activeTab === tab
                  ? 'bg-[#353B48] text-white shadow-sm'
                  : 'bg-[#F3F4F6] text-[#353B48] hover:bg-gray-200'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Requests List (buyer-portal submissions) */}
      {activeTab === 'Requests' ? (
        loadingRequests ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <SkeletonInspection key={i} />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {inspectionRequests.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow border border-gray-200">
                <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No inspection requests yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Buyer-portal registrations and proposed times will appear here.
                </p>
              </div>
            ) : (
              inspectionRequests.map((req) => (
                <InspectionRequestCard
                  key={req.id}
                  request={req}
                  isActioning={requestActionId === req.id}
                  onConfirm={() => handleConfirmRequest(req)}
                  onCancel={() => handleCancelRequest(req)}
                />
              ))
            )}
          </div>
        )
      ) : null}

      {/* Inspections List */}
      {activeTab !== 'Requests' && (loadingStates.inspections ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <SkeletonInspection key={i} />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {inspectionsList.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow border border-gray-200">
              <Home className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No inspections found</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-[#55C5D0] hover:text-[#3bb3be] font-medium"
              >
                Schedule your first OFI
              </button>
            </div>
          ) : (
            inspectionsList.map((inspection) => (
              <InspectionCard
                key={inspection.id}
                inspection={inspection}
                onDelete={handleDeleteInspection}
                onEdit={(insp) => {
                  setEditingInspectionId(insp.id);
                  setFormData({
                    property: insp.property_id || insp.property?.id || insp.property || '',
                    scheduled_date: formatMelbourneDatePart(insp.scheduled_date),
                    scheduled_time: formatMelbourneTimePart(insp.scheduled_date),
                    duration_minutes: insp.duration_minutes || 30,
                    notes: insp.notes || '',
                    // Default ON when editing too. This used to prefill from
                    // !!insp.calendar_event_id, which meant editing an open home
                    // that had no event yet sent create_calendar_event=false and
                    // opted the edit OUT of the calendar sync entirely — the
                    // opposite of what an agent changing the time expects
                    // (Monday 12757422009). The backend creates the event when
                    // it's missing and updates it when it isn't.
                    create_calendar_event: true
                  });
                  setShowCreateModal(true);
                }}
                onAddBuyer={() => {
                  setAddingBuyerTo(inspection);
                  setShowAddBuyerModal(true);
                }}
                onViewBuyers={() => navigate(`/open-home/${inspection.id}`)}
                onShowQr={(insp) => setQrModalInspection(insp)}
              />
            ))
          )}
        </div>
      ))}

      {/* Add Buyer Modal */}
      {showAddBuyerModal && (
        <AddBuyerModal
          onClose={() => setShowAddBuyerModal(false)}
          formData={buyerFormData}
          setFormData={setBuyerFormData}
          inspection={addingBuyerTo}
          submitting={loadingStates.submit}
          onSubmit={async (e) => {
            e.preventDefault();
            if (!addingBuyerTo || loadingStates.submit) return;

            // Rental open homes speak "renter" end to end, including the toasts.
            const terms = inspectionTerms(addingBuyerTo);

            const fullName = (buyerFormData.name || '').trim();
            if (!fullName) {
              toast.error(`Please enter the ${terms.nounLower}'s full name.`);
              return;
            }

            setLoadingStates(prev => ({ ...prev, submit: true }));

            try {
              const interestMap = {
                'COLD': 'cold',
                'WARM': 'warm',
                'HOT': 'hot'
              };

              const nameParts = fullName.split(/\s+/);
              const firstName = nameParts[0] || '';
              const lastName = nameParts.slice(1).join(' ');
              const email = (buyerFormData.email || '').trim();
              const phone = (buyerFormData.phone || '').trim();

              const payload = {
                // Legacy fields — kept for older backend deployments
                name: fullName,
                phone,
                first_name: firstName,
                last_name: lastName,
                mobile: phone,
                email: email || null,
                checkin_method: 'agent_device',
                interest_level: interestMap[buyerFormData.temperature] || 'warm',
                property_to_sell: buyerFormData.has_property_to_sell || null,
                additional_notes: [
                  buyerFormData.notes,
                  (terms.showContractRequested && buyerFormData.contract_requested) ? '[Contract Requested]' : '',
                  buyerFormData.has_property_to_sell ? '[Has Property to Sell]' : ''
                ].filter(Boolean).join('\n')
              };

              await checkInBuyerToSession(addingBuyerTo, payload);

              // Invalidate caches to ensure counts update
              apiCache.invalidatePattern('inspections-');
              apiCache.invalidate('inspections-analytics');
              if (viewingBuyersFor?.id === addingBuyerTo.id) {
                apiCache.invalidate(`inspection-attendees-${addingBuyerTo.id}`);
              }

              toast.success(
                email
                  ? `${terms.noun} added to inspection! A confirmation email has been sent.`
                  : `${terms.noun} added to inspection!`
              );
              setShowAddBuyerModal(false);

              // Reset form
              setBuyerFormData({
                name: '',
                phone: '',
                email: '',
                notes: '',
                contract_requested: false,
                has_property_to_sell: false,
                temperature: 'WARM' // Default
              });

              loadInitialData();
            } catch (error) {
              console.error(`Error adding ${terms.nounLower}:`, error);
              toast.error(formatApiError(error, `Failed to add ${terms.nounLower}`));
            } finally {
              setLoadingStates(prev => ({ ...prev, submit: false }));
            }
          }}
        />
      )}

      {/* View Buyers Modal */}
      {showViewBuyersModal && viewingBuyersFor && (
        <ViewBuyersModal
          inspection={viewingBuyersFor}
          attendees={attendees}
          loading={loadingStates.attendees}
          onClose={() => {
            setShowViewBuyersModal(false);
            setViewingBuyersFor(null);
            setAttendees([]);
          }}
          onRefresh={() => loadAttendees(viewingBuyersFor.id)}
          onConvertToLead={handleConvertToLead}
        />
      )}

      {/* Signage QR modal — opened from any inspection row */}
      {qrModalInspection && (
        <OpenHomeQrModal
          inspection={qrModalInspection}
          inspectionId={qrModalInspection.id}
          onClose={() => setQrModalInspection(null)}
        />
      )}

      {/* Per-agent "QR Link to Portal" — invite people to sign up, credited to this agent */}
      {showAgentQrModal && (
        <AgentPortalQrModal
          agent={user}
          onClose={() => setShowAgentQrModal(false)}
        />
      )}

      {/* Create Open Home (new flow) Modal */}
      {showOpenHomeModal && (
        <CreateOpenHomeModal
          properties={properties}
          agents={agents}
          onClose={() => setShowOpenHomeModal(false)}
          onCreated={() => {
            // Refresh inspection list so the new session shows up immediately
            apiCache.invalidatePattern('inspections-');
            loadInitialData(true);
          }}
        />
      )}

      {/* Create Inspection Modal */}
      {showCreateModal && (
        <CreateInspectionModal
          formData={formData}
          setFormData={setFormData}
          properties={properties}
          loadingProperties={loadingStates.properties}
          loadingSubmit={loadingStates.submit}
          onSubmit={handleCreateInspection}
          editingInspectionId={editingInspectionId}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && inspectionToDelete && (
        <div className="fixed inset-0 bg-[#353B48]/40 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl border border-gray-100">
            <h3 className="text-xl font-bold text-[#353B48] mb-4 text-left">
              Delete Inspection
            </h3>
            <p className="text-[#6B7280] mb-8 leading-relaxed text-left">
              Are you sure you want to delete the inspection for <span className="font-bold text-[#353B48]">{inspectionToDelete.property_address || inspectionToDelete.property_title || 'this property'}</span>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 text-left">
              <button
                disabled={isDeleting}
                onClick={() => {
                  setShowDeleteModal(false);
                  setInspectionToDelete(null);
                }}
                className="px-6 py-2.5 text-gray-500 font-bold bg-gray-50 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-sans"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteInspection}
                disabled={isDeleting}
                className="px-6 py-2.5 text-white font-bold bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all shadow-md shadow-red-200 font-sans flex items-center gap-2"
              >
                {isDeleting && (
                  <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                )}
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoized Inspection Card Component
const InspectionCard = React.memo(({
  inspection,
  onAddBuyer,
  onViewBuyers,
  onDelete,
  onEdit,
  onShowQr,
}) => {
  // Render in Melbourne time, not the viewer's browser timezone, so the card
  // agrees with REA / the buyer portal for a non-Melbourne viewer.
  const endInstant = new Date(new Date(inspection.scheduled_date).getTime() + (inspection.duration_minutes || 0) * 60000);
  const startTime = formatMelbourneClock(inspection.scheduled_date);
  const endTime = formatMelbourneClock(endInstant);
  const dayLabel = formatMelbourneDayLabel(inspection.scheduled_date);
  // "Add Renter" on a lease listing, "Add Buyer" on everything else.
  const terms = inspectionTerms(inspection);

  return (
    <div className="bg-[#F9FAFB] rounded-xl border border-[#E4E6E9] p-6 relative group transition-all hover:bg-white hover:shadow-sm">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center text-left gap-4">
        {/* Left Side: Info */}
        <div className="flex-1 w-full">
          <h3 className="text-lg font-bold text-[#353B48] mb-1 pr-16 sm:pr-0">
            {inspection.property_address || inspection.property_title || '111/405 High Street Northcote'}
          </h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
            <div className="flex items-center gap-1.5 text-sm font-bold text-[#353B48]">
              <Calendar className="w-4 h-4 text-[#55C5D0]" />
              {dayLabel}
            </div>
            <div className="flex items-center gap-1.5 text-sm font-bold text-[#55C5D0]">
              <Clock className="w-4 h-4" />
              {startTime} – {endTime}
            </div>
          </div>
          <div className="flex items-center text-sm text-[#6B7280] font-medium">
            <div className="w-5 h-5 rounded-full bg-[#353B48] flex items-center justify-center mr-2">
              <span className="text-[10px] text-white font-bold">C</span>
            </div>
            {inspection.agent_name || 'Chris Vrantsis'}
          </div>
        </div>

        {/* Right Side: Buttons */}
        <div className="w-full sm:w-auto sm:mr-24 flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => onShowQr && onShowQr(inspection)}
            className="w-full sm:w-auto bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-4 py-2.5 rounded-lg font-bold text-sm transition-all tracking-wide flex items-center justify-center gap-2"
            title="QR Link to Portal"
          >
            <QrCode className="w-4 h-4" />
            QR Link to Portal
          </button>
          <button
            onClick={onViewBuyers}
            className="w-full sm:w-auto bg-[#55C5D0] hover:bg-[#45b5c0] text-white px-6 py-2.5 rounded-lg font-bold text-sm transition-all tracking-wide flex items-center justify-center gap-2"
          >
            <Users className="w-4 h-4" />
            Open session ({inspection.total_attendees || 0})
          </button>
          <button
            onClick={onAddBuyer}
            className="w-full sm:w-auto bg-[#353B48] hover:bg-[#2c313a] text-white px-6 py-2.5 rounded-lg font-bold text-sm transition-all tracking-wide flex items-center justify-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            {terms.addLabel}
          </button>
        </div>
      </div>

      {/* Action Icons - Top Right */}
      <div className="absolute top-4 right-4 flex gap-2">
        <button
          onClick={() => onEdit && onEdit(inspection)}
          className="p-2 text-gray-500 hover:text-[#55C5D0] bg-white border border-[#E4E6E9] rounded-lg transition-all shadow-sm"
          title="Edit"
        >
          <Edit className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete && onDelete(inspection)}
          className="p-2 text-gray-500 hover:text-red-500 bg-white border border-[#E4E6E9] rounded-lg transition-all shadow-sm"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
});

// Add Buyer Modal Component
const AddBuyerModal = React.memo(({ onClose, formData, setFormData, inspection, onSubmit, submitting }) => {
  // Rental open homes take renters, not buyers, and have no contract of sale.
  const terms = inspectionTerms(inspection);

  // Typeahead — re-add a known contact without re-keying their details
  // (CRM-2026-00039). Mirrors the agent-device check-in on OpenHomeSessionPage:
  // as the agent types the name, search existing contacts and, on pick, fill
  // name/phone/email so they aren't entered by hand again.
  const inspectionId = inspection?.id;
  const nounLower = (terms.nounLower || terms.noun || 'buyer').toLowerCase();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [picked, setPicked] = useState(false);
  const searchTimer = useRef(null);

  // Search on name, phone OR email — buyers sometimes give a number or email
  // first (CRM-2026-00039 revision). The backend query already matches all
  // three, so any field can drive the same lookup; whichever field the agent
  // is typing into owns the dropdown.
  const runSearch = (q) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = (q || '').trim();
    if (!inspectionId || term.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await searchInspectionBuyers(inspectionId, term);
        setSuggestions(Array.isArray(res.data) ? res.data : []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 250);
  };

  const onFieldChange = (field) => (e) => {
    const value = e.target.value;
    setFormData({ ...formData, [field]: value });
    setPicked(false);
    setActiveField(field);
    runSearch(value);
  };

  const pickSuggestion = (s) => {
    setFormData({
      ...formData,
      name: s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim(),
      phone: s.phone || '',
      email: s.email || '',
    });
    setShowSuggestions(false);
    setSuggestions([]);
    setPicked(true);
  };

  // Shared dropdown, rendered under whichever field is active.
  const renderSuggestions = (field) => (
    showSuggestions && activeField === field && suggestions.length > 0 && (
      <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-64 overflow-auto">
        {suggestions.map((s) => (
          <li key={s.contact_id}>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2"
            >
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-900 truncate">{s.name}</span>
                <span className="block text-[11px] text-slate-500 truncate">{[s.phone, s.email].filter(Boolean).join(' · ') || 'No contact details'}</span>
              </span>
              {s.times_viewed > 0 && (
                <span className="shrink-0 text-[10px] font-semibold bg-[#EAF8FE] text-[#2E9BD6] rounded-full px-2 py-0.5">
                  {s.times_viewed === 1 ? 'Viewed once' : s.times_viewed === 2 ? 'Viewed twice' : `Viewed ${s.times_viewed} times`}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    )
  );

  return (
    <div className="fixed inset-0 bg-[#353B48]/40 flex items-center justify-center p-4 z-[60] overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl font-sans overflow-hidden">
        <div className="p-8">
          <div className="mb-6 text-left">
            <h2 className="text-xl font-bold text-[#353B48]">Add {terms.noun} to Inspection</h2>
            <p className="text-sm text-[#9CA3AF] mt-1">Manage property inspections and track attendees</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-6 text-left">
            {/* Buyer / Renter Details Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <User className="w-4 h-4 text-[#353B48]" />
                <span className="text-sm font-bold text-[#353B48]">{terms.noun} Details</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1 relative">
                  <label className="text-xs font-bold text-[#353B48]">Full Name *</label>
                  <input
                    type="text"
                    required
                    autoComplete="off"
                    placeholder={`Start typing to find an existing ${nounLower}…`}
                    className="w-full px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] text-sm"
                    value={formData.name}
                    onChange={onFieldChange('name')}
                    onFocus={() => { if (activeField === 'name' && suggestions.length) setShowSuggestions(true); }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  />
                  {picked && (
                    <span className="absolute right-3 top-[30px] text-[10px] text-emerald-600 font-semibold">✓ details filled</span>
                  )}
                  {renderSuggestions('name')}
                </div>
                <div className="space-y-1 relative">
                  <label className="text-xs font-bold text-[#353B48]">Phone</label>
                  <input
                    type="text"
                    autoComplete="off"
                    placeholder={`Type a phone to find an existing ${nounLower}…`}
                    className="w-full px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] text-sm"
                    value={formData.phone}
                    onChange={onFieldChange('phone')}
                    onFocus={() => { if (activeField === 'phone' && suggestions.length) setShowSuggestions(true); }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  />
                  {renderSuggestions('phone')}
                </div>
                <div className="space-y-1 relative">
                  <label className="text-xs font-bold text-[#353B48]">Email</label>
                  <input
                    type="email"
                    autoComplete="off"
                    className="w-full px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] text-sm"
                    value={formData.email}
                    onChange={onFieldChange('email')}
                    onFocus={() => { if (activeField === 'email' && suggestions.length) setShowSuggestions(true); }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    placeholder="Type an email to find them, or leave for portal invite"
                  />
                  {renderSuggestions('email')}
                </div>
              </div>

              <div className="mt-4 space-y-1">
                <label className="text-xs font-bold text-[#353B48]">Notes</label>
                <textarea
                  rows="3"
                  className="w-full px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] text-sm resize-none"
                  placeholder="Any additional notes about this contact..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>

            {/* Buyer / Renter Info Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <User className="w-4 h-4 text-[#353B48]" />
                <span className="text-sm font-bold text-[#353B48]">{terms.noun} Info</span>
              </div>

              <div className="flex gap-6 mb-4">
                {terms.showContractRequested && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 border-[#E5E7EB] rounded text-[#55C5D0] focus:ring-[#55C5D0]"
                      checked={formData.contract_requested}
                      onChange={(e) => setFormData({ ...formData, contract_requested: e.target.checked })}
                    />
                    <span className="text-xs font-medium text-[#353B48]">Contract Requested</span>
                  </label>
                )}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 border-[#E5E7EB] rounded text-[#55C5D0] focus:ring-[#55C5D0]"
                    checked={formData.has_property_to_sell}
                    onChange={(e) => setFormData({ ...formData, has_property_to_sell: e.target.checked })}
                  />
                  <span className="text-xs font-medium text-[#353B48]">Has Property to sell</span>
                </label>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, temperature: 'COLD' })}
                  className={`py-2 rounded-lg font-bold text-xs transition-all ${formData.temperature === 'COLD' ? 'bg-[#56CCF2] text-white shadow-lg shadow-[#56CCF2]/30' : 'bg-[#EAF8FE] text-[#56CCF2]'
                    }`}
                >
                  COLD
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, temperature: 'WARM' })}
                  className={`py-2 rounded-lg font-bold text-xs transition-all ${formData.temperature === 'WARM' ? 'bg-[#F2994A] text-white shadow-lg shadow-[#F2994A]/30' : 'bg-[#FFF4E8] text-[#F2994A]'
                    }`}
                >
                  WARM
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, temperature: 'HOT' })}
                  className={`py-2 rounded-lg font-bold text-xs transition-all ${formData.temperature === 'HOT' ? 'bg-[#3ACA6F] text-white shadow-lg shadow-[#3ACA6F]/30' : 'bg-[#EBF9F0] text-[#3ACA6F]'
                    }`}
                >
                  HOT
                </button>
              </div>
            </div>

            {/* Form Footer */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-8 py-2 border border-[#E5E7EB] rounded-lg text-sm font-bold text-[#6B7280] hover:bg-gray-50 transition-all font-sans"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-8 py-2 bg-[#55C5D0] hover:bg-[#45b5c0] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-sm font-bold transition-all shadow-md shadow-[#55C5D0]/20 font-sans"
              >
                {submitting ? 'Adding…' : terms.addLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

InspectionCard.displayName = 'InspectionCard';
AddBuyerModal.displayName = 'AddBuyerModal';

// Inspection Request Card — buyer-portal submissions
const InspectionRequestCard = React.memo(({ request, isActioning, onConfirm, onCancel }) => {
  const whenLabel = request.when
    ? `${formatMelbourneDayLabel(request.when)} • ${formatMelbourneClock(request.when)}`
    : 'No date';

  const statusStyle = {
    PENDING: 'bg-amber-100 text-amber-800',
    CONFIRMED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-red-100 text-red-800',
    NO_SHOW: 'bg-gray-100 text-gray-800',
    ATTENDED: 'bg-blue-100 text-blue-800',
  }[request.status] || 'bg-gray-100 text-gray-800';

  const isActionable = request.status === 'PENDING' || (request.status === 'CONFIRMED' && request.is_proposed);

  return (
    <div className="bg-[#F9FAFB] rounded-xl border border-[#E4E6E9] p-6 transition-all hover:bg-white hover:shadow-sm">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex-1 w-full">
          <div className="flex items-center flex-wrap gap-2 mb-2">
            <h3 className="text-lg font-bold text-[#353B48]">
              {request.property?.address || 'Property'}
            </h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusStyle}`}>
              {request.status}
            </span>
            {request.is_proposed && (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-800">
                Proposed Time
              </span>
            )}
          </div>
          <div className="text-sm font-bold text-[#55C5D0] mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {whenLabel}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-[#353B48]">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-[#6B7280]" />
              <span className="font-semibold">{request.name}</span>
              {request.attendees > 1 && (
                <span className="text-xs text-[#6B7280]">+{request.attendees - 1}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#6B7280]" />
              <a href={`mailto:${request.email}`} className="text-[#55C5D0] hover:underline truncate">
                {request.email}
              </a>
            </div>
            {request.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#6B7280]" />
                <a href={`tel:${request.phone}`} className="text-[#55C5D0] hover:underline">
                  {request.phone}
                </a>
              </div>
            )}
            {request.agent?.name && (
              <div className="flex items-center gap-2 text-[#6B7280]">
                <span className="text-xs font-bold">AGENT:</span>
                <span>{request.agent.name}</span>
              </div>
            )}
          </div>

          {request.message && (
            <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xs font-bold text-[#6B7280] mb-1">Message</p>
              <p className="text-sm text-[#353B48]">{request.message}</p>
            </div>
          )}

          <p className="text-xs text-[#9CA3AF] mt-3">
            Submitted {request.created_at ? moment(request.created_at).fromNow() : ''}
          </p>
        </div>

        {isActionable && (
          <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2 flex-shrink-0">
            {request.status === 'PENDING' && (
              <button
                onClick={onConfirm}
                disabled={isActioning}
                className="bg-[#55C5D0] hover:bg-[#45b5c0] text-white px-5 py-2.5 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <CheckCircle2 className="w-4 h-4" />
                {request.is_proposed ? 'Confirm & Schedule OFI' : 'Confirm'}
              </button>
            )}
            {request.status !== 'CANCELLED' && (
              <button
                onClick={onCancel}
                disabled={isActioning}
                className="bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-5 py-2.5 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <XCircle className="w-4 h-4" />
                Cancel
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

InspectionRequestCard.displayName = 'InspectionRequestCard';

// View Buyers Modal Component
const ViewBuyersModal = React.memo(({
  inspection,
  attendees,
  loading,
  onClose,
  onRefresh,
  onConvertToLead
}) => {
  const getTemperatureColor = (temperature) => {
    const colors = {
      'COLD': 'bg-[#56CCF2] text-white',
      'WARM': 'bg-[#F2994A] text-white',
      'HOT': 'bg-[#3ACA6F] text-white',
      'cold': 'bg-[#56CCF2] text-white',
      'warm': 'bg-[#F2994A] text-white',
      'hot': 'bg-[#3ACA6F] text-white',
      'low': 'bg-gray-100 text-gray-700',
      'medium': 'bg-blue-100 text-blue-700',
      'high': 'bg-orange-100 text-orange-700',
      'very_high': 'bg-green-100 text-green-700'
    };
    return colors[temperature] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatMelbourneDateTime(dateString);
  };

  return (
    <div className="fixed inset-0 bg-[#353B48]/40 flex items-center justify-center p-3 sm:p-4 z-[60] overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl font-sans overflow-hidden my-4 sm:my-8">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl font-bold text-[#353B48]">Buyers List</h2>
              <p className="text-sm text-[#9CA3AF] mt-1">
                {inspection?.property_address || inspection?.property_title || 'Inspection'} • {attendees.length} {attendees.length === 1 ? 'buyer' : 'buyers'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onRefresh}
                disabled={loading}
                className="px-4 py-2 text-sm font-bold text-[#55C5D0] border border-[#55C5D0] rounded-lg hover:bg-[#55C5D0] hover:text-white transition-all disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#55C5D0]"></div>
            </div>
          ) : attendees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium mb-2">No buyers added yet</p>
              <p className="text-sm text-gray-500">Add buyers to this inspection to see them here</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {attendees.map((buyer, index) => (
                <div
                  key={buyer.id || index}
                  className="bg-[#F9FAFB] border border-[#E4E6E9] rounded-xl p-4 sm:p-5 hover:bg-white hover:shadow-md transition-all"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    {/* Left: Buyer Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#55C5D0] flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-bold text-[#353B48] mb-1 truncate">
                            {buyer.name || 'Unknown Buyer'}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-[#6B7280] mb-2">
                            {buyer.email && (
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {buyer.email}
                              </span>
                            )}
                            {buyer.phone && (
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                {buyer.phone}
                              </span>
                            )}
                          </div>
                          {buyer.check_in_time && (
                            <p className="text-xs text-[#9CA3AF] flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Checked in: {formatDate(buyer.check_in_time)}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Additional Info */}
                      {(buyer.interest_level || buyer.budget_range || buyer.timeline || buyer.feedback) && (
                        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200 space-y-2">
                          {buyer.interest_level && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-[#6B7280]">Interest:</span>
                              <span className={`px-2 py-1 rounded text-xs font-bold ${getTemperatureColor(buyer.interest_level)}`}>
                                {buyer.interest_level.replace('_', ' ').toUpperCase()}
                              </span>
                            </div>
                          )}
                          {buyer.budget_range && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-[#6B7280]">Budget:</span>
                              <span className="text-xs text-[#353B48]">{buyer.budget_range}</span>
                            </div>
                          )}
                          {buyer.timeline && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-[#6B7280]">Timeline:</span>
                              <span className="text-xs text-[#353B48]">{buyer.timeline}</span>
                            </div>
                          )}
                          {buyer.feedback && (
                            <div className="mt-2">
                              <span className="text-xs font-bold text-[#6B7280] block mb-1">Feedback:</span>
                              <p className="text-xs text-[#353B48] bg-white p-2 rounded border border-gray-200">
                                {buyer.feedback}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right: Actions */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-shrink-0">
                      {buyer.converted_to_lead && (
                        <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-bold">
                          Converted to Lead
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

ViewBuyersModal.displayName = 'ViewBuyersModal';

const CreateInspectionModal = React.memo(({
  formData,
  setFormData,
  properties,
  loadingProperties,
  loadingSubmit,
  onSubmit,
  onClose,
  editingInspectionId = null
}) => {
  // Only live On Market / Off Market listings can have an inspection time put
  // out against them (CRM-2026-00079). Withdrawn, sold, leased and
  // appraisal-stage properties are not for sale with us any more. The property
  // already on the form is kept so the edit flow can still open on it.
  const selectableProperties = schedulableProperties(properties, formData.property);
  return (
    <div className="fixed inset-0 bg-[#353B48]/40 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">{editingInspectionId ? 'Edit Inspection' : 'Schedule Open For Inspection'}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Building className="w-4 h-4 mr-1" />
                Property *
                {selectableProperties.length === 0 && !loadingProperties && (
                  <span className="ml-2 text-xs text-red-500">(No properties available)</span>
                )}
              </label>
              {loadingProperties ? (
                <div className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
              ) : selectableProperties.length === 0 ? (
                <div className="w-full px-3 py-2 border border-red-300 rounded-md bg-red-50 text-red-700 text-sm">
                  No properties found. Please create a property first.
                </div>
              ) : (
                <select
                  value={formData.property}
                  onChange={(e) => {
                    console.log('Property selected:', e.target.value);
                    setFormData({ ...formData, property: e.target.value });
                  }}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                >
                  <option value="">Select a property</option>
                  {selectableProperties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.address || property.title}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 mr-1" />
                  Date *
                </label>
                <DateInput
                  value={formData.scheduled_date}
                  onChange={(v) => setFormData({ ...formData, scheduled_date: v })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md flex items-center justify-between gap-2 text-left focus:outline-none focus:ring-2 focus:ring-[#55C5D0] bg-white"
                />
              </div>

              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4 mr-1" />
                  Time *
                </label>
                <TimeInput
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (minutes)
              </label>
              <select
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              >
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={20}>20 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="3"
                placeholder="Any special instructions or notes..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="calendar_event"
                checked={formData.create_calendar_event}
                onChange={(e) => setFormData({ ...formData, create_calendar_event: e.target.checked })}
                className="w-4 h-4 text-[#55C5D0] border-gray-300 rounded focus:ring-[#55C5D0]"
              />
              <label htmlFor="calendar_event" className="text-sm text-gray-700">
                Create Google Calendar event
              </label>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> A unique QR code will be generated for attendees to check-in using their mobile devices.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loadingSubmit}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loadingSubmit}
                className="px-6 py-2 bg-[#55C5D0] text-white rounded-md hover:bg-[#3bb3be] disabled:opacity-50 flex items-center"
              >
                {loadingSubmit ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingInspectionId ? 'Updating...' : 'Scheduling...'}
                  </>
                ) : (
                  editingInspectionId ? 'Update Inspection' : 'Schedule OFI'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

CreateInspectionModal.displayName = 'CreateInspectionModal';

export default React.memo(InspectionsPage);
